export class ValidationError extends Error {
  constructor(
    message: string,
    public readonly path: string,
    public readonly expected: string,
    public readonly received: unknown,
  ) {
    super(message)
    this.name = 'ValidationError'
  }
}

type TypeGuard<T> = (value: unknown, path: string) => value is T

function isString(value: unknown, path: string): value is string {
  if (typeof value !== 'string') throw new ValidationError(`Se esperaba string en ${path}`, path, 'string', value)
  return true
}

function isNumber(value: unknown, path: string): value is number {
  if (typeof value !== 'number' || isNaN(value)) throw new ValidationError(`Se esperaba number en ${path}`, path, 'number', value)
  return true
}

function isBoolean(value: unknown, path: string): value is boolean {
  if (typeof value !== 'boolean') throw new ValidationError(`Se esperaba boolean en ${path}`, path, 'boolean', value)
  return true
}

function isNull(value: unknown, path: string): value is null {
  if (value !== null) throw new ValidationError(`Se esperaba null en ${path}`, path, 'null', value)
  return true
}

function optional<T>(guard: TypeGuard<T>): TypeGuard<T | null | undefined> {
  return (value: unknown, path: string): value is T | null | undefined => {
    if (value === null || value === undefined) return true
    return guard(value, path)
  }
}

function nullable<T>(guard: TypeGuard<T>): TypeGuard<T | null> {
  return (value: unknown, path: string): value is T | null => {
    if (value === null) return true
    return guard(value, path)
  }
}

function arrayOf<T>(guard: TypeGuard<T>): TypeGuard<T[]> {
  return (value: unknown, path: string): value is T[] => {
    if (!Array.isArray(value)) throw new ValidationError(`Se esperaba array en ${path}`, path, 'array', value)
    value.forEach((item, i) => guard(item, `${path}[${i}]`))
    return true
  }
}

function shape<T extends Record<string, TypeGuard<any>>>(schema: T): TypeGuard<{ [K in keyof T]: T[K] extends TypeGuard<infer U> ? U : never }> {
  return (value: unknown, path: string): value is any => {
    if (typeof value !== 'object' || value === null) {
      throw new ValidationError(`Se esperaba objeto en ${path}`, path, 'object', value)
    }
    const obj = value as Record<string, unknown>
    for (const key of Object.keys(schema)) {
      if (key in obj) {
        schema[key](obj[key], `${path}.${key}`)
      }
    }
    return true
  }
}

function partial<T extends Record<string, TypeGuard<any>>>(schema: T): TypeGuard<Partial<{ [K in keyof T]: T[K] extends TypeGuard<infer U> ? U : never }>> {
  return (value: unknown, path: string): value is any => {
    if (typeof value !== 'object' || value === null) {
      throw new ValidationError(`Se esperaba objeto en ${path}`, path, 'object', value)
    }
    const obj = value as Record<string, unknown>
    for (const key of Object.keys(schema)) {
      if (key in obj && obj[key] !== undefined) {
        schema[key](obj[key], `${path}.${key}`)
      }
    }
    return true
  }
}

function unionOf<T extends TypeGuard<any>[]>(...guards: T): TypeGuard<any> {
  return (value: unknown, path: string): value is any => {
    const errors: string[] = []
    for (const guard of guards) {
      try {
        guard(value, path)
        return true
      } catch (e) {
        errors.push((e as ValidationError).message)
      }
    }
    throw new ValidationError(
      `Ninguna variante de union coincide en ${path}: ${errors.join('; ')}`,
      path,
      `union(${guards.length})`,
      value,
    )
  }
}

export const v = {
  string: isString,
  number: isNumber,
  boolean: isBoolean,
  null: isNull,
  optional,
  nullable,
  array: arrayOf,
  shape,
  partial,
  union: unionOf,
}

export function validateOrLog<T>(label: string, data: unknown, guard: TypeGuard<T>): data is T {
  try {
    return guard(data, label)
  } catch (e) {
    if (e instanceof ValidationError) {
      console.warn(`[Store Validation] ${e.message}`)
    }
    return false
  }
}

export function assertValid<T>(label: string, data: unknown, guard: TypeGuard<T>): asserts data is T {
  if (!validateOrLog(label, data, guard)) {
    if (import.meta.env.DEV) {
      console.warn(`[Store Validation] Dato inválido en ${label}:`, data)
    }
  }
}
